import { useState } from "react";
import { Send } from "lucide-react";
import { resolveIcon } from "@/cms/icons";

const DEFAULT_ITEMS = [
  { icon: "Mail", label: "Email Us", value: "hello@exqura.com", sub: "We reply within 24 hours" },
  { icon: "Phone", label: "Call Us", value: "+65 6123 4567", sub: "Mon – Fri, 9am – 6pm SGT" },
  { icon: "MapPin", label: "Our Office", value: "Singapore", sub: "1 Raffles Place, #20-61" },
  { icon: "Clock", label: "Support Hours", value: "24 / 7", sub: "For urgent booking assistance" },
];

const DEFAULT_TEXT = {
  detailsHeading: "Reach Out Directly",
  formHeading: "Send Us a Message",
  nameLabel: "Name",
  namePlaceholder: "Your name",
  emailLabel: "Email",
  emailPlaceholder: "your@email.com",
  subjectLabel: "Subject",
  subjectPlaceholder: "How can we help?",
  messageLabel: "Message",
  messagePlaceholder: "Tell us more...",
  submitLabel: "Send Message",
  successTitle: "Message Sent!",
  successText: "Thank you for reaching out. Our team will get back to you within 24 hours.",
  resetLabel: "Send another message",
};

/** Everything on this block is CMS-editable: the contact methods
 * (fields.items) and every heading/label/placeholder on the message form.
 * The form's behavior (local state, submit) stays in code. */
export default function ContactDetails({ fields = {} }) {
  const { items = DEFAULT_ITEMS } = fields;
  const t = (key) => fields[key] || DEFAULT_TEXT[key];
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <section className="pb-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left — contact details */}
        <div className="space-y-6">
          <h2 className="text-white font-bold text-2xl mb-6">{t("detailsHeading")}</h2>
          {items.map((c, i) => {
            const Icon = resolveIcon(c.icon);
            return (
              <div key={i} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-widest mb-0.5">{c.label}</p>
                  <p className="text-white font-semibold text-base">{c.value}</p>
                  <p className="text-white/40 text-sm">{c.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right — form */}
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
          {sent ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center mb-4">
                <Send className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-bold text-2xl mb-2">{t("successTitle")}</h3>
              <p className="text-white/60 text-sm max-w-xs">
                {t("successText")}
              </p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                className="mt-6 text-[#D4AF37] text-sm font-semibold hover:underline"
              >
                {t("resetLabel")}
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-white font-bold text-2xl mb-6">{t("formHeading")}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{t("nameLabel")}</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("namePlaceholder")}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{t("emailLabel")}</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={t("emailPlaceholder")}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{t("subjectLabel")}</label>
                  <input
                    type="text"
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder={t("subjectPlaceholder")}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{t("messageLabel")}</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t("messagePlaceholder")}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold rounded-xl px-6 py-3.5 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  {t("submitLabel")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
